import { useState, useEffect, memo } from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useBookImageRefresh } from "@/hooks/use-book-cache-invalidation";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { isImageCached } from "@/lib/imageCache";

interface EnhancedBookCoverProps {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  onLoad?: () => void;
  bookId?: number;
  cacheVersion?: number;
  showSkeleton?: boolean;
}

export const EnhancedBookCover = memo(function EnhancedBookCover({
  src,
  alt,
  className,
  priority = false,
  onLoad,
  bookId,
  cacheVersion,
  showSkeleton = true
}: EnhancedBookCoverProps) {
  // Check if image is already cached - if so, skip loading state entirely
  const isCached = src ? isImageCached(src) : false;
  
  const [isLoading, setIsLoading] = useState(!isCached);
  const [hasError, setHasError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(isCached);

  // Netflix-style intersection observer - once loaded, stays loaded
  const { ref: containerRef, isIntersecting, hasBeenVisible } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '200px', // Start loading well before visible
    freezeOnceVisible: true // Keep images loaded when scrolling back up
  });

  // Listen for book image updates - only refresh when this specific book changes
  useBookImageRefresh(bookId, () => {
    setRefreshKey(prev => prev + 1);
    setIsLoading(true);
    setHasError(false);
    setHasLoadedOnce(false);
  });
  
  useEffect(() => {
    // Don't reset loading state if we've already loaded this image
    if (!hasLoadedOnce) {
      setIsLoading(true);
      setHasError(false);
    }
  }, [src, refreshKey, hasLoadedOnce]);

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasLoadedOnce(true); // Mark as loaded - Netflix-style persistence
    if (onLoad) onLoad();
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const imageUrl = (() => {
    try {
      if (!src || typeof src !== 'string') {
        return src || '';
      }
      
      // Only add cache busting when we actually need to refresh (refreshKey > 0)
      // or when we have a specific cache version that differs from what's expected
      if (refreshKey > 0 || cacheVersion) {
        if (src.startsWith('http://') || src.startsWith('https://')) {
          const url = new URL(src);
          
          if (cacheVersion) {
            url.searchParams.set('v', cacheVersion.toString());
          }
          
          if (refreshKey > 0) {
            url.searchParams.set('refresh', refreshKey.toString());
          }
          
          return url.toString();
        }
        
        // For relative URLs, add cache busting only when needed
        const separator = src.includes('?') ? '&' : '?';
        let cacheBuster = '';
        
        if (cacheVersion) {
          cacheBuster += `v=${cacheVersion}`;
        }
        
        if (refreshKey > 0) {
          cacheBuster += (cacheBuster ? '&' : '') + `refresh=${refreshKey}`;
        }
        
        return cacheBuster ? src + separator + cacheBuster : src;
      }
      
      // Normal case: just return the URL as-is to let browser caching work
      return src;
    } catch (error) {
      console.warn('Invalid URL in EnhancedBookCover:', src, error);
      return src;
    }
  })();

  // Determine if we should actually load the image
  // Cached images always load immediately, priority images always load, otherwise wait for intersection
  const shouldLoad = isCached || priority || isIntersecting || hasBeenVisible || hasLoadedOnce;

  return (
    <div ref={containerRef} className="relative w-full h-full overflow-hidden rounded-[8px]">
      {/* Skeleton loader */}
      {showSkeleton && isLoading && !hasLoadedOnce && (
        <Skeleton className="absolute inset-0 rounded-[8px]" />
      )}
      
      {/* Error state */}
      {hasError ? (
        <div className="w-full h-full bg-muted flex items-center justify-center text-muted-foreground rounded-[8px]">
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
          </svg>
        </div>
      ) : shouldLoad ? (
        <img
          src={imageUrl}
          alt={alt}
          className={cn(
            "w-full h-full object-cover rounded-[8px] transition-opacity duration-500 ease-out",
            isLoading && !hasLoadedOnce ? "opacity-0" : "opacity-100",
            className
          )}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      ) : (
        // Show skeleton placeholder until image should load
        showSkeleton && <Skeleton className="absolute inset-0 rounded-[8px]" />
      )}
    </div>
  );
});
